import React, { ChangeEvent, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { UserPlus } from "lucide-react";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_SECTION_GAP,
  StatStrip,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook";
import TablePanel from "features/admin/NewLook/TablePanel";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import { useHistory } from "react-router-dom";
import { sharedLib } from "@shared/services/shared.service";
import {
  ClientData,
  UsageType,
  InvitationStatus,
  LanguageNameSpaces,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  LARGE_MOBILE,
  SMALL_SCREEN,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Input from "common/Inputs/Input";
import {
  UserOutlined,
  ApiOutlined,
  MailOutlined,
  SearchOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useUsageLimits } from "@shared/features/settings/hooks.ts";
import usePlanUsage from "@shared/features/settings/subscription/hooks/usePlanUsage";
import {
  getLocaleStringWithPrecision,
  smartRound,
} from "@shared/utils/numbers";
import LineSkeleton from "common/Skeleton/LineSkeleton.tsx";
import { UserGroup } from "features/users-management/types";
import UserRow, { UserRowSkeleton } from "features/users-management/UserRow";
import { hexToRGB, isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";
import { useUser } from "@shared/utils/hooks/misc";
import Progress from "common/Progress/Progress";
import Button from "common/Buttons/Button";
import EmptyView from "common/Empty/EmptyView";
import ManageInvitationsModal from "features/configuration/InviteExternalUsers/ManageInvitationsModal.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc.tsx";
import { EXCHANGE_TO_PRIMARY_EXCHANGE } from "@shared/common/constants.ts";

const UsersManagement = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.USERS_MANAGEMENT);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [manageInvitationsModalOpen, setManageInvitationsModalOpen] =
    useState(false);
  const { user } = useUser();
  useDisablePageScroll();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isNewLook = theme.isNewLook;

  const { data: clients, isLoading: isClientsLoading } =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();

  const isClientsActuallyLoading = isClientsLoading && !clients;

  const {
    allQuotaUsage,
    userUsageLimits,
    isLoadingQuotas: isUsageLoading,
  } = usePlanUsage();

  const { getExtraQuota } = useUsageLimits();
  const extraClientSlots = getExtraQuota(UsageType.CLIENT) || 0;

  const { data: invites, isLoading: isInvitesLoading } =
    sharedLib.sharedApi.configuration.useGetAccountInvitesQuery();

  const toggleUser = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const getClientUserId = (client: ClientData) =>
    client.sub_user_uid?.toString() || client.sub_user_invite_prefix;

  const allUsers = useMemo(() => {
    if (!clients) return [];

    const groupedUsers: Record<
      number,
      UserGroup & { slotsRecord: Record<string, ClientData[]> }
    > = {};

    const sortedClients = [...clients].sort(
      (a, b) => (a.creation_date || 0) - (b.creation_date || 0)
    );

    sortedClients
      .filter((client) => !!getClientUserId(client))
      .forEach((client) => {
        const userId = getClientUserId(client)!;
        if (!groupedUsers[userId]) {
          groupedUsers[userId] = {
            sub_user_invite_prefix:
              client.sub_user_invite_prefix || t("unknown_user"),
            sub_user_uid: userId,
            raw_sub_user_uid: client.sub_user_uid,
            sub_user_permission_level: client.sub_user_permission_level,
            apiSlots: [],
            totalAccounts: 0,
            slotsRecord: {},
          };
        }

        const user = groupedUsers[userId];
        user.totalAccounts++;

        const groupingKey = client.client_unique_identifier;

        if (!user.slotsRecord[groupingKey]) {
          user.slotsRecord[groupingKey] = [];
        }
        user.slotsRecord[groupingKey].push(client);
      });

    const processedUsers = Object.values(groupedUsers).map((user) => {
      user.apiSlots = Object.entries(user.slotsRecord).map(
        ([, accounts], index) => ({
          exchange: EXCHANGE_TO_PRIMARY_EXCHANGE[accounts[0].exchange],
          accounts,
          slotNumber: index + 1,
        })
      );
      return user;
    });

    return processedUsers.sort((a, b) =>
      a.sub_user_invite_prefix.localeCompare(b.sub_user_invite_prefix)
    );
  }, [clients, t]);

  const users = useMemo(() => {
    return allUsers.filter(
      (user) =>
        user.sub_user_invite_prefix
          ?.toLowerCase()
          .includes(searchTerm?.toLowerCase()) ||
        user.raw_sub_user_uid
          ?.toString()
          .toLowerCase()
          .includes(searchTerm?.toLowerCase()) ||
        user.sub_user_uid
          ?.toString()
          .toLowerCase()
          .includes(searchTerm?.toLowerCase())
    );
  }, [allUsers, searchTerm]);

  const noClientsConnected =
    !isClientsLoading && allUsers.length === 0 && !searchTerm;

  const { data: portfolioData, isLoading: isPortfolioLoading } =
    sharedLib.sharedApi.portfolio.useGetPortfolioDataQuery(undefined);

  const accountBalances = useMemo(() => {
    const balances: Record<number, number> = {};
    if (!portfolioData?.data) return balances;

    Object.entries(portfolioData.data).forEach(([key, exchangeData]) => {
      if (key === "total" || key === "available") return;

      Object.entries(exchangeData).forEach(([accKey, accountData]) => {
        if (accKey === "total" || accKey === "available") return;

        const data = accountData as any;
        if (data.id !== undefined && data.total?.USD !== undefined) {
          balances[data.id] = data.total.USD;
        }
      });
    });

    return balances;
  }, [portfolioData]);

  const kpis = useMemo(() => {
    const totalUsers = allUsers.length;

    const apiSlotsLimit =
      Number(userUsageLimits?.[UsageType.CLIENT] ?? 0) +
      Number(extraClientSlots);

    const pendingInvitationsCount =
      invites?.filter((i) => i.status === InvitationStatus.PENDING).length ?? 0;

    const apiSlotsUsed = allQuotaUsage?.[UsageType.CLIENT] ?? 0;

    let totalBalance = 0;
    allUsers.forEach((user) => {
      user.apiSlots.forEach((slot) => {
        slot.accounts.forEach((account) => {
          totalBalance += accountBalances[account.client_id] || 0;
        });
      });
    });

    const showPurchaseApiButton =
      isWhitelabelSubscriptionType(user?.subscription_plan_type) &&
      !user?.isExpired();

    return [
      {
        title: t("total_users"),
        value: isClientsActuallyLoading ? (
          <div style={{ maxWidth: 40 }}>
            <LineSkeleton height={28} />
          </div>
        ) : (
          totalUsers
        ),
        newLookValue: isClientsActuallyLoading ? (
          <Skeleton width={32} height={22} />
        ) : (
          totalUsers
        ),
        icon: <UserOutlined />,
        color: theme.colors.green,
      },
      {
        title: t("api_slots_used"),
        value: isUsageLoading ? (
          <div style={{ maxWidth: 80 }}>
            <LineSkeleton height={28} />
          </div>
        ) : (
          `${apiSlotsUsed} / ${apiSlotsLimit}`
        ),
        newLookValue: isUsageLoading ? (
          <Skeleton width={72} height={22} />
        ) : (
          `${apiSlotsUsed} / ${apiSlotsLimit}`
        ),
        newLookAction: showPurchaseApiButton ? (
          <NewLookLinkButton
            type="button"
            onClick={() => history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)}
          >
            {t("purchase")}
          </NewLookLinkButton>
        ) : undefined,
        icon: <ApiOutlined />,
        newLookProgress:
          !isUsageLoading && apiSlotsLimit > 0
            ? Math.min(100, (Number(apiSlotsUsed) / apiSlotsLimit) * 100)
            : undefined,
        progress:
          apiSlotsLimit > 0 && typeof apiSlotsUsed === "number"
            ? Math.min(100, (apiSlotsUsed / apiSlotsLimit) * 100)
            : undefined,
        color: theme.colors.purple,
        action: showPurchaseApiButton ? (
          <KPICardActionButton
            onClick={() => history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)}
          >
            {t("purchase")}
          </KPICardActionButton>
        ) : undefined,
      },
      {
        title: t("pending_invitations"),
        value: isInvitesLoading ? (
          <div style={{ maxWidth: 40 }}>
            <LineSkeleton height={28} />
          </div>
        ) : (
          pendingInvitationsCount
        ),
        newLookValue: isInvitesLoading ? (
          <Skeleton width={32} height={22} />
        ) : (
          pendingInvitationsCount
        ),
        icon: <MailOutlined />,
        color: theme.colors.orange,
        action: (
          <KPICardActionButton
            onClick={() => setManageInvitationsModalOpen(true)}
          >
            {t("invite_user")}
          </KPICardActionButton>
        ),
      },
      {
        title: t("total_balance"),
        value: isPortfolioLoading ? (
          <div style={{ maxWidth: 100 }}>
            <LineSkeleton height={28} />
          </div>
        ) : (
          `$${getLocaleStringWithPrecision(smartRound(totalBalance, 2))}`
        ),
        newLookValue: isPortfolioLoading ? (
          <Skeleton width={96} height={22} />
        ) : (
          `$${getLocaleStringWithPrecision(smartRound(totalBalance, 2))}`
        ),
        icon: <DollarOutlined />,
        color: theme.colors.primaryBlue,
      },
    ];
  }, [
    allUsers,
    allQuotaUsage,
    userUsageLimits,
    isUsageLoading,
    invites,
    isInvitesLoading,
    accountBalances,
    isPortfolioLoading,
    user,
    isClientsActuallyLoading,
    theme,
  ]);

  if (isClientsActuallyLoading && !isNewLook) {
    return <LoadingSpinnerOverlay isLoading={true} spinnerSize="large" />;
  }

  const usersList = (
    <div>
      {noClientsConnected ? (
        <EmptyView
          iconKey={EmptyStatesType.Users}
          title={t("empty_title")}
          description={t("empty_description")}
          descriptionStyle={{ maxWidth: 350 }}
          buttonText={t("create_invite")}
          buttonOnClick={() => setManageInvitationsModalOpen(true)}
          hasData={false}
          iconWidth={90}
        />
      ) : (
        users.map((user) => (
          <UserRow
            key={user.sub_user_uid}
            user={user}
            isExpanded={expandedUsers.has(user.sub_user_uid)}
            onToggle={() => toggleUser(user.sub_user_uid)}
            accountBalances={accountBalances}
            isPortfolioLoading={isPortfolioLoading}
          />
        ))
      )}
    </div>
  );

  const searchInput = (
    <SearchInput
      placeholder={t("search_placeholder")}
      prefix={<SearchOutlined />}
      allowClear={true}
      value={searchTerm}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        setSearchTerm(e.target.value)
      }
    />
  );

  // New look: the key figures in a strip, then the users as one panel of
  // rows - the search and the invite action in its header.
  if (isNewLook) {
    const inviteButton = (
      <Button
        type="primary"
        onClick={() => setManageInvitationsModalOpen(true)}
      >
        <UserPlus size={15} strokeWidth={NEW_LOOK_ICON_STROKE} />
        {t("invite_user")}
      </Button>
    );
    const renderUsers = () => {
      if (isClientsActuallyLoading)
        return [0, 1, 2].map((index) => <UserRowSkeleton key={index} />);
      if (noClientsConnected)
        return (
          <PanelEmpty
            title={t("empty_title")}
            description={t("empty_description")}
          />
        );
      if (!users.length)
        return (
          <PanelEmpty
            title={tCommon("empty.no_results_matching_search", {
              objectName: tCommon("page_title.users").toLowerCase(),
            })}
          />
        );
      return users.map((user) => (
        <UserRow
          key={user.sub_user_uid}
          user={user}
          isExpanded={expandedUsers.has(user.sub_user_uid)}
          onToggle={() => toggleUser(user.sub_user_uid)}
          accountBalances={accountBalances}
          isPortfolioLoading={isPortfolioLoading}
        />
      ));
    };
    return (
      <PageWrapper>
        <NewLookContent $isBottomNavigator={isBottomNavigator}>
          <NewLookStats
            items={kpis.map((kpi, i) => ({
              key: String(i),
              label: kpi.title,
              value: kpi.newLookValue,
              meta:
                kpi.newLookProgress !== undefined || kpi.newLookAction ? (
                  <NewLookKpiMeta>
                    {kpi.newLookProgress !== undefined && (
                      <NewLookBar>
                        <NewLookBarFill
                          style={{ width: `${kpi.newLookProgress}%` }}
                        />
                      </NewLookBar>
                    )}
                    {kpi.newLookAction}
                  </NewLookKpiMeta>
                ) : undefined,
            }))}
          />
          <TablePanel
            title={tCommon("page_title.users")}
            count={allUsers.length || undefined}
            actions={
              isBottomNavigator ? (
                inviteButton
              ) : (
                <>
                  {searchInput}
                  {inviteButton}
                </>
              )
            }
            flush={true}
            data-testid="users-management-users"
          >
            {isBottomNavigator && (
              <NewLookPhoneToolbar>{searchInput}</NewLookPhoneToolbar>
            )}
            <div role="list">{renderUsers()}</div>
          </TablePanel>
          <ManageInvitationsModal
            open={manageInvitationsModalOpen}
            onCancel={() => setManageInvitationsModalOpen(false)}
          />
        </NewLookContent>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <KPISection>
          {kpis.map((kpi, i) => (
            <KPICard key={i}>
              <KPICardHeader>
                <KPIIconWrapper $color={kpi.color}>{kpi.icon}</KPIIconWrapper>
                {kpi.action}
              </KPICardHeader>
              <KPIValue as="div">{kpi.value}</KPIValue>
              <KPITitleWrapper>
                <Text type="secondary">{kpi.title}</Text>
              </KPITitleWrapper>

              {kpi.progress !== undefined && (
                <Progress
                  strokeColor={{
                    "0%": theme.colors.primaryBlue,
                    "100%": theme.colors.blues[3],
                  }}
                  percent={kpi.progress}
                  showInfo={false}
                  style={{ marginTop: 8 }}
                />
              )}
            </KPICard>
          ))}
        </KPISection>

        <MainContainer>
          <TableActions>{searchInput}</TableActions>

          {usersList}
        </MainContainer>
        <ManageInvitationsModal
          open={manageInvitationsModalOpen}
          onCancel={() => setManageInvitationsModalOpen(false)}
        />
      </ContentContainer>
    </PageWrapper>
  );
};

export default UsersManagement;

const PageWrapper = styled.div`
  background: ${(p) => p.theme.colors.layoutBgColor};
  width: 100%;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  z-index: 1;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The page sits on the canvas like every other new-look page. */
      background: transparent;
    `}
`;

const ContentContainer = styled.div`
  padding: 40px;
  gap: 32px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: ${SMALL_SCREEN}) {
    padding: 24px;
    gap: 24px;
  }

  @media (max-width: ${LARGE_MOBILE}) {
    padding: 16px;
    gap: 16px;
  }
`;

const KPISection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;
const KPICard = styled.div`
  background: ${(p) => p.theme.colors.cardBgColor};
  border: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: auto;

  @media (max-width: ${LARGE_MOBILE}) {
    padding: 16px;
  }
`;
const KPIIconWrapper = styled.div<{ $color: string }>`
  flex: none;
  width: 32px;
  height: 32px;
  background: ${(p) => hexToRGB(p.$color, 0.1)};
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 16px;
`;
const KPICardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;
const KPICardActionButton = styled.div`
  cursor: pointer;
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 14px;
  display: flex;
  align-items: center;
  font-weight: 500;
  transition: opacity 0.2s;
  text-align: right;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 380px) {
    font-size: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 13px;
      line-height: 20px;
    `}
`;
const KPIValue = styled(Text)`
  font-size: 28px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: 500px) {
    font-size: 20px;
  }
`;
const KPITitleWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  width: 100%;
`;
const MainContainer = styled.div`
  background: ${(p) => p.theme.colors.cardBgColor};
  border: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  border-radius: 12px;

  @media (max-width: ${LARGE_MOBILE}) {
    border-radius: 8px;
  }
`;
const TableActions = styled.div`
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: ${SMALL_SCREEN}) {
    flex-direction: column;
    align-items: stretch;
  }

  @media (max-width: ${LARGE_MOBILE}) {
    padding: 16px;
  }
`;
const SearchInput = styled(Input)`
  width: 300px;
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      width: 240px;
      border-radius: ${p.theme.radii.control}px;
    `}

  @media (max-width: ${LARGE_MOBILE}) {
    width: 100%;
  }

  /* New look: a regular field (its own border, hover and focus states). */
  ${(p) =>
    !p.theme.isNewLook &&
    css`
      &&& {
        background: ${p.theme.colors.cardBgColor} !important;
        border: 1px solid ${p.theme.colors.lightBorderGrey} !important;

        .ant-input {
          background: transparent !important;
          font-size: 13px;
        }
      }
    `}

  .ant-input-prefix {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }
`;

const NewLookContent = styled.div<{ $isBottomNavigator: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 32px;
  ${(p) =>
    p.$isBottomNavigator &&
    css`
      gap: 12px;
      padding: 12px 16px 24px;
    `}
`;
const NewLookStats = styled(StatStrip)`
  @media (min-width: 601px) {
    > div {
      padding: 20px 24px;
    }
  }
  /* Let the labels wrap instead of truncating in narrow cells. */
  @media (max-width: 700px) {
    > div > div:first-child {
      white-space: normal;
    }
  }
`;
const NewLookKpiMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  min-width: 0;
  width: 100%;
`;
const NewLookBar = styled.div`
  flex: none;
  width: 72px;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  overflow: hidden;
`;
const NewLookBarFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: ${(p) => getNewLookTokens(p.theme).accent};
`;
const NewLookLinkButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
  }
`;
const NewLookPhoneToolbar = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  .ant-input-affix-wrapper {
    width: 100%;
  }
`;
